import type { InboundReplyRow, ReplyQueueStatus } from '../types';

export type ReplyQueue = 'new' | 'follow_up' | 'done' | 'all';

export function replyQueueStatus(reply: InboundReplyRow): ReplyQueueStatus {
  return reply.review?.queue_status ?? 'new';
}

export function replyQueueFor(reply: InboundReplyRow): Exclude<ReplyQueue, 'all'> {
  const status = replyQueueStatus(reply);
  if (status === 'follow_up') return 'follow_up';
  if (status === 'acknowledged' || status === 'dismissed') return 'done';
  return 'new';
}

export function replyMatchesQueue(reply: InboundReplyRow, queue: ReplyQueue): boolean {
  return queue === 'all' || replyQueueFor(reply) === queue;
}

export function replyQueueCounts(replies: InboundReplyRow[]): Record<ReplyQueue, number> {
  const counts: Record<ReplyQueue, number> = {
    new: 0,
    follow_up: 0,
    done: 0,
    all: replies.length,
  };
  for (const reply of replies) counts[replyQueueFor(reply)] += 1;
  return counts;
}

function timestamp(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Follow-ups sort by due date first; the other queues remain most-recent-first. */
export function sortReplyQueue(replies: InboundReplyRow[], queue: ReplyQueue): InboundReplyRow[] {
  return [...replies].sort((a, b) => {
    if (queue === 'follow_up') {
      const aDue = timestamp(a.review?.follow_up_due_at) || Number.MAX_SAFE_INTEGER;
      const bDue = timestamp(b.review?.follow_up_due_at) || Number.MAX_SAFE_INTEGER;
      if (aDue !== bDue) return aDue - bDue;
    }
    const aStamp = timestamp(a.review?.updated_at ?? a.created_at);
    const bStamp = timestamp(b.review?.updated_at ?? b.created_at);
    return bStamp - aStamp;
  });
}
